import { Suspense, useCallback } from "react"
import { FormProvider, SubmitHandler, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { useFragment } from "react-relay"
import { zodResolver } from "@hookform/resolvers/zod"
import { TrashSimple } from "@phosphor-icons/react"
import { useUser } from "@/apis/bff"
import { useDirtyDrawerMaker } from "@/components/drawer-wrapper"
import { Button } from "@/components/ui/button"
import {
  Drawer,
  DrawerBody,
  DrawerClose,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer"
import { FaroInsightsWrapper } from "@/grafana-faro"
import { withErrorBoundary } from "@/hoc/with-error-boundary"
import { useIsFirstRender } from "@/hooks/useIsFirstRender"
import { toast } from "@/hooks/useToast"
import { LastModificationInfo } from "@/modules/patients/detail/last-modification-info"
import { deMedicationFragmentInline$data } from "@/modules/patients/detail/medication/de/services/__generated__/deMedicationFragmentInline.graphql"
import { DePrescriptionContent } from "@/modules/patients/detail/prescription/de/de-prescription-content"
import { Prescription, PrescriptionSchema } from "@/modules/patients/detail/prescription/de/schema/prescription-schema"
import { dePrescriptionFragment$key } from "@/modules/patients/detail/prescription/de/services/__generated__/dePrescriptionFragment.graphql"
import { dePrescriptionFragment } from "@/modules/patients/detail/prescription/de/services/dePrescriptionFragment"
import { useDeCreatePrescriptionMutation } from "@/modules/patients/detail/prescription/de/services/useDeCreatePrescriptionMutation"
import { useDeDeletePrescriptionMutation } from "@/modules/patients/detail/prescription/de/services/useDeDeletePrescriptionMutation"
import { useDeGeneratePrescriptionMutation } from "@/modules/patients/detail/prescription/de/services/useDeGeneratePrescriptionMutation"
import { useDeUpdatePrescriptionMutation } from "@/modules/patients/detail/prescription/de/services/useDeUpdatePrescriptionMutation"
import { useDePrescriptionStoreContext } from "@/modules/patients/detail/prescription/de/store/usePrescriptionStore"
import { usePatientParam } from "@/modules/patients/hooks/usePatientParam"
import { useConfirmStore } from "@/stores/useConfirmStore"
import { useDrawerStore } from "@/stores/useDrawerStore"

interface DePrescriptionDrawerProps {
  progressNoteId: string
  prescriptionRef?: dePrescriptionFragment$key
  id?: string
  existingMedication?: deMedicationFragmentInline$data
}

const DePrescriptionDrawer = withErrorBoundary(({ progressNoteId, id, prescriptionRef }: DePrescriptionDrawerProps) => {
  const { t } = useTranslation()
  const { isOneOf } = useUser()
  const hasSavePermission = isOneOf(["Doctor", "MedicalSupervisor"])
  const isFirstRender = useIsFirstRender()

  const closeDrawer = useDrawerStore((state) => state.closeDrawer)
  const unSavedMedicationIds = useDePrescriptionStoreContext((state) => state.unSavedMedicationIds)

  const resetPrescriptionStore = useDePrescriptionStoreContext((state) => state.reset)
  const removedMedicationIds = useDePrescriptionStoreContext((state) => state.removedMedicationIds)
  const confirm = useConfirmStore((state) => state.confirm)
  const cancel = useConfirmStore((state) => state.cancel)

  const [createPrescription, isCreating] = useDeCreatePrescriptionMutation()
  const [updatePrescription, isUpdating] = useDeUpdatePrescriptionMutation()
  const [deletePrescription] = useDeDeletePrescriptionMutation()
  const [generatePrescriptionDocument, isGenerating] = useDeGeneratePrescriptionMutation()

  const patientId = usePatientParam()
  const isEditMode = !!id
  const isAddMode = !isEditMode && patientId && progressNoteId
  const persistedData = useFragment(dePrescriptionFragment, prescriptionRef ?? null)
  const updatedAt = persistedData?.updatedAt ?? persistedData?.createdAt
  const updatedBy = persistedData?.updatedBy ?? persistedData?.createdBy
  const persistedMedicationIds = persistedData?.medications.length ? persistedData.medications.map((i) => i.id) : []

  const getFilteredMedicationIds = useCallback(
    (medicationIds: string[]) => {
      const filteredMedicationIds = medicationIds.filter((id) => {
        // When existing medications get removed -> Filter those ones out from persisted medication ids
        return !removedMedicationIds.includes(id)
      })

      // Add items from un-saved medication ids to persisted medication ids
      const updatedMedicationIds = Array.from(new Set([...filteredMedicationIds, ...unSavedMedicationIds]))
      return updatedMedicationIds
    },
    [unSavedMedicationIds, removedMedicationIds],
  )

  const parsedData =
    isEditMode && persistedData && isFirstRender
      ? PrescriptionSchema.parse({
          medicationIds: getFilteredMedicationIds(persistedMedicationIds),
        })
      : null

  const form = useForm<Prescription>({
    mode: "all",
    resolver: zodResolver(PrescriptionSchema),
    defaultValues:
      isEditMode && parsedData
        ? parsedData
        : {
            medicationIds: unSavedMedicationIds,
          },
  })

  const {
    handleSubmit,
    formState: { isDirty },
  } = form

  const handleCloseDrawer = () => {
    resetPrescriptionStore()
    closeDrawer()
  }

  const onSubmit: SubmitHandler<Prescription> = (data) => {
    if (isAddMode) {
      createPrescription({
        variables: {
          input: {
            progressNoteId,
            patientId,
            medicationIds: data.medicationIds,
          },
        },
        onCompleted(response) {
          const id = response.de_createPrescription.de_Prescription?.id
          if (id) {
            handleGeneratePrescriptionDocument(id)
          }
        },
        updater(store, data) {
          try {
            const progressNoteRecordProxy = store.get(progressNoteId)
            const prescription = data?.de_createPrescription?.de_Prescription

            if (prescription?.id) {
              const newRecordProxy = store.get(prescription.id)
              if (newRecordProxy) {
                progressNoteRecordProxy?.setLinkedRecord(newRecordProxy, "de_prescription")
              }
            }
          } catch (error) {
            console.error("Error while processing data:", error)
          }
        },
      })
    }

    if (isEditMode) {
      updatePrescription({
        variables: {
          input: {
            id,
            medicationIds: data.medicationIds,
          },
        },
        onCompleted(response) {
          const id = response.de_updatePrescription.de_Prescription?.id
          if (id) {
            handleGeneratePrescriptionDocument(id)
          }
        },
      })
    }
  }

  const onDelete = () => {
    if (progressNoteId && id) {
      deletePrescription({
        variables: {
          input: {
            id,
          },
        },
        onCompleted() {
          handleCloseDrawer()
          toast({
            description: t("patient.prescription.deleteMessage.success"),
          })
          cancel()
        },
      })
    }
  }

  const handleDelete = () => {
    confirm({
      title: t("patient.prescription.deleteConfirmTitle"),
      content: t("episodeOfCare.deleteProgressNote.content"),
      onConfirm: onDelete,
      confirmVariant: "danger",
      confirmText: t("common.button.delete"),
      cancelText: t("common.button.keep"),
    })
  }

  const handleGeneratePrescriptionDocument = (id: string) => {
    if (id) {
      generatePrescriptionDocument({
        id,
        onCompleted() {
          handleCloseDrawer()
        },
      })
    }
  }

  const hasUnsavedChanges = isDirty || unSavedMedicationIds.length > 0 || removedMedicationIds.length > 0
  useDirtyDrawerMaker(hasUnsavedChanges)

  return (
    <Drawer open onOpenChange={handleCloseDrawer}>
      <FormProvider {...form}>
        <DrawerContent>
          <DrawerClose className="top-2 right-2" />
          <DrawerHeader className="pb-4" data-testid="prescription-header">
            <DrawerTitle className="animate-fade-in-bottom text-on-surface-subdued text-sm font-medium">
              {t("patient.prescription.prescription")}
            </DrawerTitle>
          </DrawerHeader>
          <DrawerBody data-testid="prescription-body">
            <Suspense fallback={null}>
              <DePrescriptionContent
                unSavedMedicationIds={unSavedMedicationIds}
                prescriptionRef={prescriptionRef}
                id={id}
                progressNoteId={progressNoteId}
              />
            </Suspense>
          </DrawerBody>
          <DrawerFooter className="flex-wrap gap-2" data-testid="prescription-footer">
            {isEditMode && (
              <div className="flex gap-2">
                {updatedAt && updatedBy && <LastModificationInfo updatedAt={updatedAt} updatedBy={updatedBy} />}
                <button onClick={handleDelete} data-testid="prescription-delete-button">
                  <TrashSimple className="text-on-surface-subdued" />
                </button>
              </div>
            )}
            <div className="ml-auto flex gap-2">
              <Button data-testid="prescription-cancel-button" variant="secondary" onClick={handleCloseDrawer}>
                {t("common.button.cancel")}
              </Button>
              <Button
                disabled={!hasSavePermission}
                data-testid="prescription-save-button"
                isLoading={isCreating || isUpdating || isGenerating}
                onClick={() => {
                  FaroInsightsWrapper.pushEvent("documents_prescription_save")
                  handleSubmit(onSubmit)()
                }}>
                {t("common.button.save")}
              </Button>
            </div>
          </DrawerFooter>
        </DrawerContent>
      </FormProvider>
    </Drawer>
  )
}, "DePrescriptionDrawer")

export { DePrescriptionDrawer, type DePrescriptionDrawerProps }
